import React, { useRef } from 'react';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useStorage } from '../hooks/useStorage';
import { motion } from 'motion/react';

interface DocumentDropzoneProps {
  label: string;
  onUploadComplete: (url: string) => void;
  storagePath: string;
  accept?: string;
  existingUrl?: string | null;
  description?: string;
}

export function DocumentDropzone({ label, onUploadComplete, storagePath, accept = "application/pdf,image/*", existingUrl, description }: DocumentDropzoneProps) {
  const { uploadFile, progress, isUploading, error } = useStorage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const url = await uploadFile(file, storagePath);
      onUploadComplete(url);
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-bold text-gray-400">{label}</label>
        {existingUrl && !isUploading && (
          <span className="text-[10px] bg-emerald-500/10 text-emerald-500 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider flex items-center">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Reçu
          </span>
        )}
      </div>
      {description && <p className="text-xs text-zinc-400 -mt-2">{description}</p>}

      <div 
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.classList.add('border-[#ff5a00]', 'bg-white/5');
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.classList.remove('border-[#ff5a00]', 'bg-white/5');
        }}
        onDrop={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.classList.remove('border-[#ff5a00]', 'bg-white/5');
          const file = e.dataTransfer.files?.[0];
          if (file) {
            try {
              const url = await uploadFile(file, storagePath);
              onUploadComplete(url);
            } catch (err) {
              console.error("Upload failed", err);
            }
          }
        }}
        className={`relative group border-2 border-dashed rounded-2xl p-6 transition-all cursor-pointer flex flex-col items-center justify-center
          ${isUploading ? 'border-[#ff5a00] bg-[#ff5a00]/5' : existingUrl ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-white/10 hover:border-[#ff5a00]/50 hover:bg-white/5'}
        `}
      >
        <input 
          type="file" 
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={accept}
          className="hidden"
        />

        {isUploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-8 h-8 text-[#ff5a00] animate-spin mb-3" />
            <div className="w-48 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className="h-full bg-[#ff5a00]"
              />
            </div>
            <span className="text-xs font-bold text-[#ff5a00] mt-2">{Math.round(progress)}%</span>
          </div>
        ) : (
          <>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110
              ${existingUrl ? 'bg-emerald-500/20' : 'bg-white/5'}
            `}>
              {existingUrl ? (
                <FileText className="w-6 h-6 text-emerald-500" />
              ) : (
                <Upload className="w-6 h-6 text-gray-500 group-hover:text-[#ff5a00]" />
              )}
            </div>
            <div className="text-center">
              <span className="text-sm font-bold block mb-1">
                {existingUrl ? "Remplacer le document" : "Cliquer pour uploader"}
              </span>
              <span className="text-[10px] text-gray-500 uppercase tracking-widest font-black">
                {accept.includes('pdf') ? 'PDF' : ''} {accept.includes('image') ? '/ JPG / PNG' : ''}
              </span>
            </div>
          </>
        )}

        {error && (
          <div className="absolute -bottom-10 left-0 right-0 flex items-center justify-center text-red-500 text-xs font-bold">
            <AlertCircle className="w-3 h-3 mr-1" /> Erreur d'envoi. Réessayez.
          </div>
        )}
      </div>
    </div>
  );
}
